// Reverse synthesis (PRD M2, §8.2): existing doc → graph. Recover the
// decisions, the options implicitly on the table, and what was chosen. The
// recovered trace then flows through the same machinery as a navigated one —
// replay validation (V) and re-synthesis (S).

import { complete } from "../api/model";
import { parseDecomposition, scrubRepairArtifacts as scrub } from "../lib/json-repair";
import { traceParseOutcome } from "./telemetry";
import { save } from "./persistence";
import { decomposePrompt } from "./prompts";
import { newNode, registerNode, state, type Stakes } from "./state";

export async function reverseSynthesize(docText: string): Promise<{ branches: number; decisions: number }> {
  const doc = docText.slice(0, 7000);
  const prompt = decomposePrompt(doc);
  let data: any = null;
  for (let attempt = 0; attempt < 3 && !data; attempt++) {
    try {
      const raw = await complete(prompt, { max: 2047, temp: 0.4, kind: "decompose" });
      data = parseDecomposition(raw);
      traceParseOutcome("decompose", data ? "repaired" : "failed", raw);
    } catch { /* rate-limit blip — the next attempt may still land */ }
  }
  if (!data || !Array.isArray(data.decisions) || !data.decisions.length) throw new Error("decompose failed");

  const decisions = data.decisions.slice(0, 10).map((d: any) => {
    const options = (d.options || []).slice(0, 4).map((o: any) => ({
      label: scrub(o.label, "option"), tradeoff: scrub(o.tradeoff, ""),
    }));
    return {
      branch: scrub(d.branch, "design"),
      question: scrub(d.question, "decision"),
      options,
      chosen: Number.isInteger(d.chosen) && d.chosen >= 0 && d.chosen < options.length ? d.chosen : 0,
      stakes: (["low", "med", "high"].includes(d.stakes) ? d.stakes : "med") as Stakes,
    };
  }).filter((d: any) => d.options.length >= 2);
  if (!decisions.length) throw new Error("decompose failed");

  state.idea = scrub(data.idea, docText.slice(0, 120));

  // Root = the branch menu, one option per recovered area; each branch is a
  // chain of that area's decisions, pre-chosen as the doc chose them.
  const branches: string[] = [...new Set<string>(decisions.map((d: any) => d.branch))];
  const root = registerNode(newNode({
    branch: "root", depth: 0,
    question: "Which part of the recovered design do you want to inspect?",
    options: branches.map((b) => ({ label: b, tradeoff: "recovered from the doc" })),
    confidence: 0.6,
  }));
  state.rootId = root.id;

  branches.forEach((b, bi) => {
    let parent = root, optIdx = bi;
    for (const d of decisions.filter((x: any) => x.branch === b)) {
      const n = registerNode(newNode({
        parentId: parent.id, depth: parent.depth + 1, branch: b,
        question: d.question, options: d.options,
        chosenIndex: d.chosen, recommend: d.chosen, stakes: d.stakes,
        confidence: 0.55,   // imported, not yet validated — replay raises/flags it
      }));
      parent.childId[optIdx] = n.id;
      parent = n; optIdx = d.chosen;
    }
  });

  state.currentId = root.id;
  save();
  return { branches: branches.length, decisions: decisions.length };
}
